import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import secureLocalStorage from 'react-secure-storage';

import { Tab, Box } from '@mui/material';
import { TabList, TabPanel, TabContext } from '@mui/lab';

import { useAuth } from 'src/hooks/useAuth';
import { usePermission } from 'src/hooks/usePermission';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { fetchAgentTickets } from 'src/services/ticket.service';

import TicketTab from '../../sections/ticketTabs/ticketTab';

export default function TabTicketsPage() {
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const { id: currentUserId } = useAuth();
  const { can } = usePermission();

  const validTabs = ['ALL_TICKETS', 'MY_TICKETS', 'ASSIGNED_TO_ME', 'TAGGED_TICKETS'];

  const [tabValue, setTabValue] = useState(() => {
    const stored = secureLocalStorage.getItem('activeTicketTab');
    if (!stored || !validTabs.includes(stored)) return 'ALL_TICKETS';
    return stored;
  });

  const [assignedCount, setAssignedCount] = useState(0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    secureLocalStorage.setItem('activeTicketTab', newValue);
  };

  const hasScopePermission = can({ permission: 'TICKET:SCOPE_DEPARTMENT' });

  useEffect(() => {
    if (currentUserId) {
      fetchAgentTickets(currentUserId).then((res) =>
        setAssignedCount((res || []).length)
      );
    }
  }, [currentUserId]);

  return (
    <TabContext value={tabValue}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'transparent' }}>
        <TabList
          onChange={handleTabChange}
          aria-label="perspective tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              fontWeight: 900,
              fontSize: isSmallScreen ? '0.75rem' : '0.85rem',
            },
          }}
        >
          <Tab label={t('ticketTabs.allTickets')} value="ALL_TICKETS" />
          {hasScopePermission && (
            <Tab label={t('ticketTabs.myTickets')} value="MY_TICKETS" />
          )}
          {assignedCount > 0 && (
            <Tab
              label={`${t('ticketTabs.assignedToMe')} (${assignedCount})`}
              value="ASSIGNED_TO_ME"
            />
          )}
          <Tab label={t('ticketTabs.taggedTickets')} value="TAGGED_TICKETS" />
        </TabList>
      </Box>

      {/* Panels with standardized title props */}
      <TabPanel value="ALL_TICKETS" sx={{ p: 0 }}>
        <TicketTab title="ALL_TICKETS" />
      </TabPanel>
      <TabPanel value="MY_TICKETS" sx={{ p: 0 }}>
        <TicketTab title="MY_TICKETS" />
      </TabPanel>
      <TabPanel value="ASSIGNED_TO_ME" sx={{ p: 0 }}>
        <TicketTab title="ASSIGNED_TO_ME" />
      </TabPanel>
      <TabPanel value="TAGGED_TICKETS" sx={{ p: 0 }}>
        <TicketTab title="TAGGED_TICKETS" />
      </TabPanel>
    </TabContext>
  );
}
